import { io, Socket } from 'socket.io-client';
import { Platform } from 'react-native';
import { getSocketAPIBaseURL } from './api-url';
import { getCurrentAccessToken } from './app-auth';

function getSocketPlatform(): 'ios' | 'android' | 'web' {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
}

let socket: Socket | null = null;

type SocketLifecycleListener = (socket: Socket | null) => void;
const lifecycleListeners = new Set<SocketLifecycleListener>();

function notifySocketLifecycle(next: Socket | null): void {
  for (const listener of [...lifecycleListeners]) {
    listener(next);
  }
}

/**
 * Subscribe to singleton lifecycle changes. The callback fires with the new
 * socket instance whenever one is created and with null when it is destroyed
 * (auth sign-out/refresh), so mounted consumers can re-attach their event
 * handlers instead of listening on a dead socket. Returns an unsubscribe fn.
 */
export function subscribeSocketLifecycle(
  listener: SocketLifecycleListener,
): () => void {
  lifecycleListeners.add(listener);
  return () => {
    lifecycleListeners.delete(listener);
  };
}

/**
 * Get or create the singleton Socket.IO client.
 * Connects to the /chat namespace on the API server.
 */
export function getSocket(): Socket {
  if (socket) {
    return socket;
  }

  const baseURL = getSocketAPIBaseURL();

  const created = io(`${baseURL}/chat`, {
    transports: ['websocket', 'polling'],
    withCredentials: true,
    auth: (callback) => {
      // Platform tag lets backend route push only to genuine mobile sockets,
      // so an open web tab cannot suppress push to the user's phone.
      callback({
        token: getCurrentAccessToken() || '',
        platform: getSocketPlatform(),
      });
    },
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket = created;
  notifySocketLifecycle(created);
  return created;
}

/**
 * Disconnect and clean up the socket. Call on sign-out.
 */
export function disconnectSocket(): void {
  if (socket) {
    const current = socket;
    socket = null;
    current.disconnect();
    notifySocketLifecycle(null);
  }
}

/**
 * Check if the socket is currently connected.
 */
export function isSocketConnected(): boolean {
  return !!socket?.connected;
}
